import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { UiOrganismMainNavComponent } from '@shared/components/organisms/ui-organism-main-nav/ui-organism-main-nav.component';
import { UiAtomSpacerComponent } from '@shared/components/atoms/ui-atom-spacer/ui-atom-spacer.component';
import { UiAtomTextComponent } from '@shared/components/atoms/ui-atom-text/ui-atom-text.component';
import { UiAtomGlobalSearchComponent } from '@shared/components/atoms/ui-atom-global-search/ui-atom-global-search.component';
import { UiMoleculeMatTableComponent } from '@shared/components/molecules/ui-molecule-mat-table/ui-molecule-mat-table.component';
import { TableColumn } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-column';
import { TableAction } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-action';
import { ActionEvent } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/action-event';

import { BeneficiaryService } from '@core/services/use-cases/beneficiary.service';
import { FormService } from '@core/services/use-cases/form.service';
import { BreadcrumbService } from '@shared/services/breadcrumb.service';
import { formatDate } from '@core/utils/date.utils';
import { hasScoring } from '@core/utils/survey.utils';
import { SnackbarService } from '@shared/services/snackbar.service';
import { Beneficiary } from '@core/entities/models/beneficiary.model';
import {
  FormResponseDialogComponent,
  FormResponseData,
  FormResponseResult,
} from '@shared/components/molecules/ui-molecule-dialog-form-response/ui-molecule-dialog-form-response.component';
import {
  FormScoringDialogComponent,
  FormScoringDialogData,
} from '@shared/components/molecules/ui-molecule-dialog-form-scoring/ui-molecule-dialog-form-scoring.component';
import { DialogFormDashboardService } from '@shared/services/dialog-form-dashboard.service';
import { DialogExportColumnsService } from '@shared/services/dialog-export-columns.service';
import {
  DialogExportProgressService,
  ActiveExportDialog,
} from '@shared/services/dialog-export-progress.service';
import { ExcelExportService } from '@core/services/utils/excel-export.service';
import { FormResponsesExportService } from '@core/services/utils/form-responses-export.service';
import { ResponsePdfExportService } from '@core/services/utils/response-pdf-export.service';
import {
  UiOrganismTitleLogosComponent
} from '@shared/components/organisms/ui-organism-title-logos/ui-organism-title-logos.component';

@Component({
  selector: 'app-beneficiary-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    UiOrganismMainNavComponent,
    UiAtomSpacerComponent,
    UiAtomTextComponent,
    UiMoleculeMatTableComponent,
    UiAtomGlobalSearchComponent,
    UiOrganismTitleLogosComponent,
  ],
  templateUrl: './beneficiary-detail.html',
  styleUrl: './beneficiary-detail.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class BeneficiaryDetail implements OnInit {
  @ViewChildren(UiMoleculeMatTableComponent) tables!: QueryList<UiMoleculeMatTableComponent>;

  protected beneficiary: Beneficiary | null = null;
  protected pageTitle = '';

  /** Datos de la tabla de formularios */
  protected formsData: any[] = [];
  protected formsColumns: TableColumn[] = [];
  protected formsActions: TableAction[] = [];

  /** Estado de paginación */
  protected pageIndex = 0;
  protected pageSize = 10;
  protected totalItems = 0;
  protected isLoading = false;

  /** Filtro de búsqueda */
  private searchFilter = '';

  /** IDs de la ruta */
  private countryId = '';
  private partnerId = '';
  private beneficiaryId = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly beneficiaryService: BeneficiaryService,
    private readonly formService: FormService,
    private readonly snackbar: SnackbarService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly dashboardDialog: DialogFormDashboardService,
    private readonly dialogExportColumns: DialogExportColumnsService,
    private readonly dialogExportProgress: DialogExportProgressService,
    private readonly excelExport: ExcelExportService,
    private readonly responsesExport: FormResponsesExportService,
    private readonly responsePdfExport: ResponsePdfExportService,
  ) {}

  /** Diálogo de progreso activo durante una exportación de respuestas. */
  private activeExportDialog?: ActiveExportDialog;

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.countryId = params.get('countryId') || '';
      this.partnerId = params.get('partnerId') || '';
      this.beneficiaryId = params.get('id') || '';

      if (this.beneficiaryId) {
        this.loadBeneficiary();
        this.buildFormsColumns();
        this.loadActiveForms();
      }
    });
  }

  private loadBeneficiary(): void {
    // Single API call shared with breadcrumb service via cached promise
    const beneficiaryPromise = firstValueFrom(this.beneficiaryService.getById(this.beneficiaryId));

    // Pre-populate breadcrumb cache with the in-flight promise to avoid duplicate API calls
    const namePromise = beneficiaryPromise.then(
      (b) => `${b.firstName} ${b.lastName}`,
      () => 'Beneficiary',
    );
    this.breadcrumbService.setCachedName('beneficiary', this.beneficiaryId, namePromise);

    beneficiaryPromise.then(
      (beneficiary) => {
        this.beneficiary = beneficiary;
        this.pageTitle = `${beneficiary.firstName} ${beneficiary.lastName}`;
        this.cdr.markForCheck();
      },
      (err) => {
        this.snackbar.notifyHttpError(err);
        this.beneficiary = null;
        this.cdr.markForCheck();
      },
    );
  }

  private buildFormsColumns(): void {
    this.formsColumns = [
      {
        key: 'name',
        title: 'Name',
        titleKey: 'forms.list.table.columns.name',
        type: 'text',
        filterable: false,
      },
      {
        key: 'status',
        title: 'Status',
        titleKey: 'forms.list.table.columns.status',
        type: 'status-chip',
        width: '180px',
        filterable: false,
        getStatus: (row: any) => {
          const s = row.response?.status;
          if (s === 'SUBMITTED') {
            return {
              label: this.translate.instant('beneficiaryDetail.forms.responseStatus.submitted'),
              variant: 'success',
            };
          }
          if (s === 'DRAFT') {
            return {
              label: this.translate.instant('beneficiaryDetail.forms.responseStatus.draft'),
              variant: 'warning',
            };
          }
          return {
            label: this.translate.instant('beneficiaryDetail.forms.responseStatus.notStarted'),
            variant: 'neutral',
          };
        },
      },
      {
        key: 'createdAt',
        title: 'Created At',
        titleKey: 'forms.list.table.columns.createdAt',
        type: 'text',
        width: '150px',
        filterable: false,
      },
      {
        key: 'scoring',
        title: 'Scoring',
        titleKey: 'forms.list.table.columns.scoring',
        type: 'icon-action',
        icon: 'workspace_premium',
        iconTooltipKey: 'beneficiaryDetail.forms.viewScoring',
        width: '120px',
        filterable: false,
        showIcon: (row: any) => hasScoring(row.schema) && row.response?.status === 'SUBMITTED',
      },
    ];

    this.formsActions = [
      {
        icon: 'assignment',
        tooltip: 'Respond',
        tooltipKey: 'beneficiaryDetail.forms.respond',
        event: 'respond',
        showIf: (row: any) => row.response?.status !== 'SUBMITTED',
      },
      {
        icon: 'visibility',
        tooltip: 'View',
        tooltipKey: 'beneficiaryDetail.forms.view',
        event: 'view',
        showIf: (row: any) => row.response?.status === 'SUBMITTED',
      },
      {
        icon: 'space_dashboard',
        tooltip: 'View dashboard',
        tooltipKey: 'beneficiaryDetail.forms.viewDashboard',
        event: 'dashboard',
        disabledIf: (row: any) => row.response?.status !== 'SUBMITTED',
      },
      {
        // `file_save` pertenece a Material Symbols (no a la fuente clásica), por eso el fontSet.
        icon: 'file_save',
        iconFontSet: 'material-symbols-outlined',
        tooltip: 'Download responses',
        tooltipKey: 'beneficiaryDetail.forms.downloadResponses',
        event: 'download-responses',
        disabledIf: (row: any) => row.response?.status !== 'SUBMITTED',
      },
      {
        icon: 'picture_as_pdf',
        tooltip: 'Download PDF',
        tooltipKey: 'beneficiaryDetail.forms.downloadPdf',
        event: 'download-pdf',
        disabledIf: (row: any) => row.response?.status !== 'SUBMITTED',
      },
    ];
  }

  private loadActiveForms(): void {
    if (!this.beneficiaryId) return;

    this.isLoading = true;
    this.formService
      .getAllInBeneficiary(this.beneficiaryId, {
        offset: this.pageIndex * this.pageSize,
        limit: this.pageSize,
        filter: this.searchFilter || undefined,
      })
      .subscribe({
        next: (response) => {
          // Mapear formularios disponibles para el beneficiario
          this.formsData = response.items.map((form) => ({
            id: form.id,
            name: form.name,
            schema: (form as any).schema,
            response: (form as any).response, // ✅ Incluir respuestas guardadas
            responseStatus: (form as any).responseStatus,
            createdAt: formatDate(form.createdAt),
          }));
          this.totalItems = response.total;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.snackbar.notifyHttpError(err);
          this.formsData = [];
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  protected onFormAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;
    const form = rowData as any;

    if (event === 'respond') {
      this.openFormDialog(form, 'respond');
      return;
    }

    if (event === 'view') {
      this.openFormDialog(form, 'view');
      return;
    }

    if (event === 'scoring') {
      this.openScoringDialog(form);
      return;
    }

    if (event === 'dashboard') {
      this.openDashboardDialog(form);
      return;
    }

    if (event === 'download-responses') {
      this.onDownloadResponses(form);
      return;
    }

    if (event === 'download-pdf') {
      this.onDownloadPdf(form);
    }
  }

  /**
   * Descarga en PDF la respuesta SUBMITTED de este beneficiario para el
   * formulario indicado. Antes de generar, abre el diálogo de privacidad
   * para elegir si el nombre del beneficiario aparece en el documento y en
   * el nombre del archivo (selección vacía → PDF anónimo). El documento se
   * genera en cliente (survey-core + jsPDF), sin llamadas al backend.
   */
  private onDownloadPdf(form: any): void {
    if (form.response?.status !== 'SUBMITTED' || !this.beneficiary) return;

    const nameColumns = this.responsesExport
      .getBeneficiaryExportColumns()
      .filter((c) => c.key === 'firstName' || c.key === 'lastName');

    this.dialogExportColumns
      .open(nameColumns, {
        allowEmpty: true,
        titleKey: 'forms.response.pdf.privacyDialog.title',
        subtitleKey: 'forms.response.pdf.privacyDialog.subtitle',
        emptyHintKey: 'forms.response.pdf.privacyDialog.emptyHint',
        noteKey: 'forms.response.pdf.privacyDialog.scoringNote',
      })
      .subscribe((selectedKeys) => {
        const beneficiary = this.beneficiary;
        if (!selectedKeys || !beneficiary) return; // cancelado

        const { fileName, subtitle } = this.responsePdfExport.buildPdfNaming(
          form.name,
          [
            selectedKeys.includes('firstName') ? beneficiary.firstName : undefined,
            selectedKeys.includes('lastName') ? beneficiary.lastName : undefined,
          ],
          beneficiary.id,
        );

        this.responsePdfExport
          .downloadResponsePdf({
            formName: form.name,
            subtitle,
            schema: form.schema ?? {},
            answers: (form.response?.answers ?? {}) as Record<string, unknown>,
            fileName,
          })
          .catch(() => {
            this.snackbar.open(this.translate.instant('forms.response.messages.pdfError'));
          });
      });
  }

  /**
   * Descarga a Excel las respuestas de este beneficiario para el formulario indicado.
   * Abre el selector de columnas de beneficiario (sensibles desmarcadas por defecto),
   * construye el sheet y lo genera en el web worker mientras muestra el diálogo de progreso.
   */
  private onDownloadResponses(form: any): void {
    if (form.response?.status !== 'SUBMITTED' || !this.beneficiary) return;

    this.dialogExportColumns
      .open(this.responsesExport.getBeneficiaryExportColumns())
      .subscribe((selectedKeys) => {
        const beneficiary = this.beneficiary;
        if (!selectedKeys || selectedKeys.length === 0 || !beneficiary) return;

        this.activeExportDialog = this.dialogExportProgress.open();

        const entries = [
          {
            beneficiary,
            answers: (form.response?.answers ?? {}) as Record<string, unknown>,
          },
        ];
        const { rows, headers } = this.responsesExport.buildSheet(
          form.schema ?? {},
          entries,
          selectedKeys,
        );
        // El nombre del beneficiario solo va en el nombre del archivo si el
        // usuario ha mantenido seleccionadas esas columnas en el diálogo;
        // sin ninguna, se usa el id del beneficiario para poder identificar
        // el archivo sin exponer el nombre.
        const nameParts = [
          selectedKeys.includes('firstName') ? beneficiary.firstName : undefined,
          selectedKeys.includes('lastName') ? beneficiary.lastName : undefined,
        ].filter((p): p is string => !!p);
        const fileParts = nameParts.length > 0 ? nameParts : [beneficiary.id];
        const fileName = [form.name, ...fileParts, 'responses'].join('_');

        this.runResponsesExport(rows, headers, fileName);
      });
  }

  private runResponsesExport(
    rows: Record<string, unknown>[],
    headers: string[],
    fileName: string,
  ): void {
    if (!this.activeExportDialog) return;
    const { progress$, cancel$, close } = this.activeExportDialog;

    this.excelExport.exportRowsWithWorker(rows, headers, fileName, progress$, cancel$).subscribe({
      next: () => {
        close();
        this.activeExportDialog = undefined;
      },
      error: () => {
        close();
        this.activeExportDialog = undefined;
        this.snackbar.open(this.translate.instant('exportProgress.dialog.error'));
      },
    });
  }

  private openDashboardDialog(form: any): void {
    // Solo incluir la respuesta si está SUBMITTED — DRAFT y "sin empezar"
    // pasan array vacío y el diálogo muestra el empty state.
    const responses =
      form.response?.status === 'SUBMITTED' && form.response?.answers
        ? [form.response.answers as Record<string, unknown>]
        : [];
    const beneficiaryName = this.beneficiary
      ? `${this.beneficiary.firstName} ${this.beneficiary.lastName}`
      : '';
    const scopeLabel = beneficiaryName
      ? this.translate.instant('forms.dashboard.scope.beneficiary', { name: beneficiaryName })
      : undefined;

    this.dashboardDialog.open({
      formName: form.name,
      schema: form.schema ?? {},
      responses,
      scopeLabel,
    });
  }

  protected onRowClick(form: any): void {
    const mode = form.response?.status === 'SUBMITTED' ? 'view' : 'respond';
    this.openFormDialog(form, mode);
  }

  private openScoringDialog(form: any): void {
    const dialogData: FormScoringDialogData = {
      formName: form.name,
      schema: form.schema ?? {},
      answers: (form.response?.answers ?? {}) as Record<string, unknown>,
    };
    this.dialog.open(FormScoringDialogComponent, {
      data: dialogData,
      panelClass: ['dialog-lg', 'dialog-safe-area', 'dialog-flex'],
      width: '80vw',
      maxWidth: '1000px',
      height: '90vh',
    });
  }

  private openFormDialog(form: any, mode: 'view' | 'respond'): void {
    // Si hay respuestas guardadas, cargarlas para pre-rellenar el formulario
    const existingAnswers = form.response?.answers || undefined;

    console.log('🔍 Opening form dialog:', {
      formId: form.id,
      formName: form.name,
      mode: mode,
      hasResponse: !!form.response,
      responseStatus: form.response?.status,
      existingAnswers: existingAnswers,
    });

    const dialogData: FormResponseData = {
      title:
        mode === 'view'
          ? this.translate.instant('beneficiaryDetail.forms.viewTitle')
          : this.translate.instant('beneficiaryDetail.forms.respondTitle'),
      formName: form.name,
      schema: form.schema || {},
      mode: mode,
      beneficiaryId: this.beneficiaryId,
      formId: form.id,
      existingResponse: existingAnswers,
      beneficiaryFirstName: this.beneficiary?.firstName,
      beneficiaryLastName: this.beneficiary?.lastName,
    };

    const dialogRef = this.dialog.open(FormResponseDialogComponent, {
      data: dialogData,
      panelClass: ['dialog-xl', 'dialog-safe-area', 'dialog-flex'],
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: FormResponseResult | undefined) => {
      if (!result || !this.beneficiary) return;

      // La Thank you page (incl. el HTML de scoring si lo tiene) se muestra ahora
      // dentro del propio FormResponseDialogComponent vía doComplete(), por lo que
      // ya no re-abrimos un segundo diálogo tras el submit. Solo refrescamos la lista.
      this.loadActiveForms();
    });
  }

  protected onPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadActiveForms();
  }

  protected onGlobalSearch(searchTerm: string): void {
    this.searchFilter = searchTerm;
    if (searchTerm && searchTerm.length >= 3) {
      this.pageIndex = 0;
      this.loadActiveForms();
    } else if (!searchTerm || searchTerm.length === 0) {
      this.searchFilter = '';
      this.pageIndex = 0;
      this.loadActiveForms();
    }
  }

  /**
   * Exportar todos los formularios del beneficiario (no solo los paginados)
   * Usa el endpoint /beneficiaries/{beneficiaryId}/forms
   */
  protected onExportAllForms(event: { selectedKeys: string[] }): void {
    if (!this.beneficiaryId) return;

    this.formService.getAllForExportInBeneficiary(this.beneficiaryId, {}).subscribe({
      next: (allForms) => {
        const exportData = allForms.map((form) => {
          const responseStatus = (form as any).response?.status;
          let statusLabel: string;
          if (responseStatus === 'SUBMITTED') {
            statusLabel = this.translate.instant('beneficiaryDetail.forms.responseStatus.submitted');
          } else if (responseStatus === 'DRAFT') {
            statusLabel = this.translate.instant('beneficiaryDetail.forms.responseStatus.draft');
          } else {
            statusLabel = this.translate.instant('beneficiaryDetail.forms.responseStatus.notStarted');
          }
          return {
            id: form.id,
            name: form.name,
            status: statusLabel,
            createdAt: formatDate(form.createdAt),
          };
        });

        const formsTable = this.tables.toArray()[0];
        if (formsTable) {
          formsTable.exportFilteredDataToExcel(exportData, event.selectedKeys);
        }
      },
      error: (err) => this.snackbar.notifyHttpError(err),
    });
  }
}
